import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { TeacherTodayLessons } from "@/components/TeacherTodayLessons";
import UpcomingEvents from "@/components/UpcomingEvents";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronRight, Users, Filter, MessageSquareText, Send, Mail, CheckCircle2 } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

const ATT_OPTIONS = [
    { value: "present", label: "I pranishëm", color: "bg-sage/10 text-sage border-sage/30" },
    { value: "excused", label: "Mungon — Me arsye", color: "bg-amber-50 text-amber-700 border-amber-200" },
    { value: "unexcused", label: "Mungon — Pa arsye", color: "bg-terracotta/10 text-terracotta border-terracotta/30" },
];

const CATS = [
    { value: "very_good", label: "Shumë mirë" },
    { value: "good", label: "Mirë" },
    { value: "neutral", label: "Neutral" },
    { value: "bad", label: "Keq" },
    { value: "very_bad", label: "Shumë keq" },
    { value: "warning", label: "Vërejtje" },
];

export default function TeacherDashboard() {
    const navigate = useNavigate();
    const [classes, setClasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [filterClass, setFilterClass] = useState("all");
    const [activeSubject, setActiveSubject] = useState("");
    const [date, setDate] = useState(todayISO());
    // Map of student_id -> {status, absId}
    const [attMap, setAttMap] = useState({});
    const [discMap, setDiscMap] = useState({}); // student_id -> {warnings, suspensions, yellow_card, red_card_eligible}
    const [savingKey, setSavingKey] = useState(null);
    const [notifyingId, setNotifyingId] = useState(null);
    const [notifyOk, setNotifyOk] = useState({});
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    // Quick comment dialog
    const [cmtDlg, setCmtDlg] = useState(null);
    const [cmtForm, setCmtForm] = useState({ category: "good", text: "" });
    const [cmtBusy, setCmtBusy] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const [c, s, subs] = await Promise.all([
                api.get("/classes"),
                api.get("/teacher/students", { params: filterClass !== "all" ? { class_id: filterClass } : {} }),
                api.get("/teacher/my-subjects"),
            ]);
            setClasses(c.data); setStudents(s.data); setSubjects(subs.data);
            setActiveSubject((cur) => cur || (subs.data[0]?.id ?? ""));
        } finally { setLoading(false); }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterClass]);

    // Load discipline status for all visible students (for yellow/red card indicators)
    useEffect(() => {
        if (students.length === 0) { setDiscMap({}); return; }
        let cancelled = false;
        (async () => {
            try {
                const entries = await Promise.all(students.map((s) => api.get(`/students/${s.id}/discipline-status`).then((r) => [s.id, r.data]).catch(() => [s.id, null])));
                if (!cancelled) setDiscMap(Object.fromEntries(entries.filter(([, v]) => v)));
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [students]);

    // Load attendance — single bulk API call (fixes calendar lag)
    useEffect(() => {
        if (!activeSubject) { setAttMap({}); return; }
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/teacher/attendance", { params: { subject_id: activeSubject, date } });
                if (cancelled) return;
                const map = {};
                for (const a of (r.data.absences || [])) {
                    map[a.student_id] = { status: a.reason || "unexcused", absId: a.id };
                }
                setAttMap(map);
                setNotifyOk({});
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [activeSubject, date]);

    const setAttendance = async (studentId, status) => {
        const key = `${studentId}|${activeSubject}|${date}`;
        const current = attMap[studentId] || { status: "present", absId: null };
        if (current.status === status) return;
        setSavingKey(key); setErr("");
        try {
            if (current.status === "present" && status !== "present") {
                const r = await api.post("/teacher/absence", {
                    student_id: studentId, subject_id: activeSubject, date,
                    hours: 1, reason: status, comment: "",
                });
                setAttMap((m) => ({ ...m, [studentId]: { status, absId: r.data.id } }));
            } else if (current.status !== "present" && status === "present") {
                if (current.absId) await api.delete(`/absences/${current.absId}`);
                setAttMap((m) => {
                    const n = { ...m }; delete n[studentId]; return n;
                });
            } else if (current.absId) {
                await api.patch(`/absences/${current.absId}`, { reason: status });
                setAttMap((m) => ({ ...m, [studentId]: { status, absId: current.absId } }));
            }
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setSavingKey(null); }
    };

    const sendNotify = (s) => {
        const params = new URLSearchParams({
            audience: "single_parent",
            target_id: s.parent_id || "",
            name: s.name,
        });
        navigate(`/teacher/notify?${params.toString()}`);
    };

    const notifyAbsence = async (s, period = 1) => {
        const att = attMap[s.id];
        if (!att?.absId) {
            alert("Nxënësi është prezent. Shëno mungesën fillimisht.");
            return;
        }
        if (notifyingId) return;
        setNotifyingId(s.id);
        try {
            const r = await api.post("/teacher/absence-notify", { absence_id: att.absId, period });
            setNotifyOk((m) => ({ ...m, [s.id]: r.data.email_delivered }));
        } catch (e) {
            alert("Gabim: " + (e.response?.data?.detail || e.message));
        } finally { setNotifyingId(null); }
    };

    const openComment = (s) => {
        setCmtDlg(s);
        setCmtForm({ category: "good", text: "" });
    };

    const submitComment = async (e) => {
        e.preventDefault();
        if (!activeSubject) { alert("Zgjidh lëndën fillimisht në kontekst."); return; }
        setCmtBusy(true);
        try {
            await api.post("/teacher/comment", {
                student_id: cmtDlg.id,
                subject_id: activeSubject,
                category: cmtForm.category,
                text: cmtForm.text,
            });
            setCmtDlg(null);
        } catch (e) {
            alert("Gabim: " + (e.response?.data?.detail || e.message));
        } finally { setCmtBusy(false); }
    };

    const classMap = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c])), [classes]);

    return (
        <Layout>
            <div className="mb-6 sm:mb-8">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">Paneli i Mësuesit</div>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Klasat & Nxënësit</h1>
                <p className="mt-2 sm:mt-3 text-slate-600 max-w-2xl text-sm">Shëno mungesat, dërgo njoftime, shto komente — çdo gjë në një panel.</p>
            </div>

            <TeacherTodayLessons />

            <div className="mb-5"><UpcomingEvents days={14} limit={4} /></div>

            {/* Subject + Date selectors */}
            <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-4 mb-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 font-bold shrink-0">
                    <Filter className="w-4 h-4" /> Kontekst
                </div>
                <select value={activeSubject} onChange={(e) => setActiveSubject(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white text-sm px-3 font-semibold flex-1 sm:flex-none sm:min-w-[200px]" data-testid="teacher-subject-select">
                    <option value="">— Lënda —</option>
                    {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white text-sm px-3 font-mono" data-testid="teacher-date-select" />
                <div className="ml-auto text-xs text-slate-500">
                    {activeSubject ? <span><strong className="text-oxford">{Object.values(attMap).filter((a) => a.status !== "present").length}</strong> mungesa</span> : <span className="text-amber-700 font-semibold">Zgjidh lëndën</span>}
                </div>
            </div>

            {/* Class strip */}
            <div className="mb-5 flex flex-wrap gap-2 overflow-x-auto" data-testid="class-filter-strip">
                <button onClick={() => setFilterClass("all")} className={`px-4 py-2 rounded-full border text-sm font-semibold transition whitespace-nowrap ${filterClass === "all" ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200 text-slate-700 hover:border-oxford"}`} data-testid="filter-class-all">
                    Të gjithë ({students.length})
                </button>
                {classes.map((c) => (
                    <button key={c.id} onClick={() => setFilterClass(c.id)} className={`px-4 py-2 rounded-full border text-sm font-semibold transition whitespace-nowrap ${filterClass === c.id ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200 text-slate-700 hover:border-oxford"}`} data-testid={`filter-class-${c.id}`}>
                        {c.name} • Viti {c.year} • {c.student_count}
                    </button>
                ))}
            </div>

            {err && <div className="mb-3 text-sm text-terracotta bg-terracotta/10 border border-terracotta/30 rounded-lg px-4 py-2">{err}</div>}

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="px-4 sm:px-6 py-4 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                    <Users className="w-4 h-4 text-oxford" />
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Lista e Nxënësve</div>
                    <div className="ml-auto text-xs text-slate-500" data-testid="teacher-student-count">{students.length}</div>
                </div>
                {loading ? (
                    <div className="p-8 text-slate-500" data-testid="teacher-loading">Duke ngarkuar…</div>
                ) : students.length === 0 ? (
                    <div className="p-10 text-center text-slate-500" data-testid="teacher-no-students">Nuk keni nxënës të caktuar. Kontaktoni administratorin për t'ju caktuar lëndë + klasë.</div>
                ) : (
                    <div className="divide-y divide-slate-200">
                        {students.map((s) => {
                            const att = attMap[s.id] || { status: "present" };
                            const opt = ATT_OPTIONS.find((o) => o.value === att.status) || ATT_OPTIONS[0];
                            const key = `${s.id}|${activeSubject}|${date}`;
                            const isAbsent = att.status !== "present";
                            const okFlag = notifyOk[s.id];
                            return (
                                <div key={s.id} className="flex items-center gap-2 px-3 sm:px-6 py-3 sm:py-4 hover:bg-bone/40 transition" data-testid={`teacher-roster-row-${s.id}`}>
                                    <Link to={`/teacher/student/${s.id}`} className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 group">
                                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-oxford text-white flex items-center justify-center font-bold shrink-0 text-xs sm:text-sm">
                                            {s.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <div className="font-bold text-slate-900 truncate group-hover:text-oxford text-sm sm:text-base">{s.name}</div>
                                                {discMap[s.id]?.yellow_card && (
                                                    <span className="inline-block w-2.5 h-3.5 rounded-sm bg-amber-400 shrink-0" title="Karton i verdhë (3+ vërejtje)" data-testid={`tdash-yellow-${s.id}`}></span>
                                                )}
                                                {discMap[s.id]?.red_card_eligible && (
                                                    <span className="inline-block w-2.5 h-3.5 rounded-sm bg-terracotta shrink-0" title="Drejt përjashtimit (2+ pezullime)" data-testid={`tdash-red-${s.id}`}></span>
                                                )}
                                            </div>
                                            <div className="text-[11px] sm:text-xs text-slate-500 truncate">
                                                Viti {s.year}{s.class_id && classMap[s.class_id] ? ` • ${classMap[s.class_id].name}` : ""}
                                            </div>
                                        </div>
                                    </Link>
                                    <select
                                        value={att.status}
                                        onChange={(e) => setAttendance(s.id, e.target.value)}
                                        disabled={!activeSubject || savingKey === key}
                                        className={`h-9 rounded-md border text-[11px] sm:text-xs font-semibold px-1.5 sm:px-2 ${opt.color} disabled:opacity-50 max-w-[120px] sm:max-w-none`}
                                        data-testid={`attendance-${s.id}`}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {ATT_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-white text-slate-900">{o.label}</option>)}
                                    </select>
                                    <button
                                        onClick={() => sendNotify(s)}
                                        className="hidden sm:inline-flex p-2 rounded-md text-seablue hover:bg-seablue/10"
                                        title="Dërgo njoftim manual te prindi"
                                        data-testid={`notify-${s.id}`}
                                    >
                                        <Send className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => notifyAbsence(s, 1)}
                                        disabled={!isAbsent || notifyingId === s.id}
                                        className={`p-2 rounded-md transition ${okFlag ? "text-sage bg-sage/10" : "text-terracotta hover:bg-terracotta/10"} disabled:opacity-30 disabled:hover:bg-transparent`}
                                        title={isAbsent ? "Njofto prindin për mungesë (email auto)" : "Disponibël kur shënon mungesën"}
                                        data-testid={`absence-notify-${s.id}`}
                                    >
                                        {okFlag ? <CheckCircle2 className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                                    </button>
                                    <button
                                        onClick={() => openComment(s)}
                                        className="p-2 rounded-md text-sage hover:bg-sage/10"
                                        title="Shto koment"
                                        data-testid={`comments-${s.id}`}
                                    >
                                        <MessageSquareText className="w-4 h-4" />
                                    </button>
                                    <Link to={`/teacher/student/${s.id}`} className="text-slate-400 hover:text-oxford hidden sm:block">
                                        <ChevronRight className="w-5 h-5" />
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Quick Comment Dialog */}
            <Dialog open={!!cmtDlg} onOpenChange={(v) => !v && setCmtDlg(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Shto koment për {cmtDlg?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitComment} className="space-y-3" data-testid="quick-comment-form">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">Lënda (nga konteksti)</label>
                            <div className="text-sm font-semibold text-slate-700">{(subjects.find((s) => s.id === activeSubject) || {}).name || "— Zgjidh lëndën në kontekst lart —"}</div>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">Kategoria</label>
                            <div className="grid grid-cols-3 gap-1.5">
                                {CATS.map((c) => (
                                    <button type="button" key={c.value} onClick={() => setCmtForm({ ...cmtForm, category: c.value })}
                                        className={`px-2 py-1.5 rounded-md border text-xs font-semibold transition ${cmtForm.category === c.value ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200 hover:border-oxford"}`}
                                        data-testid={`qc-cat-${c.value}`}>{c.label}</button>
                                ))}
                            </div>
                        </div>
                        <Textarea placeholder="Tekst opsional…" value={cmtForm.text} onChange={(e) => setCmtForm({ ...cmtForm, text: e.target.value })} rows={3} data-testid="qc-text" />
                        <DialogFooter>
                            <Button type="submit" disabled={cmtBusy || !activeSubject} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="qc-submit">{cmtBusy ? "Duke ruajtur…" : "Shto Koment"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
